'use client'

import clsx from 'clsx'
import { AnimatePresence, motion } from 'framer-motion'
import React, { Fragment, useId, useState } from 'react'

export type AccordionRenderProps = {
  isExpanded: boolean
}

/** Props to spread onto a custom trigger when using the render prop or panelWrapper. Role is left to the element (e.g. tr keeps row, div should add role="button"). */
type AccordionTriggerProps = {
  'aria-controls': string | undefined
  'aria-expanded': boolean
  id: string
  onClick: () => void
  onKeyDown: (e: React.KeyboardEvent) => void
  tabIndex: number
}

type AccordionProps = {
  children: React.ReactNode
  className?: string
  defaultExpanded?: boolean
  expanded?: boolean
  onExpandedChange?: (expanded: boolean) => void
  /** When provided with trigger, wrap the panel in custom layout (e.g. table row). Trigger receives triggerProps to spread. */
  panelWrapper?: (props: { panel: React.ReactNode; isExpanded: boolean }) => React.ReactNode
  /** When provided, you render trigger and panel; spread triggerProps onto your trigger, place panel where needed. */
  render?: (props: {
    isExpanded: boolean
    panel: React.ReactNode
    triggerProps: AccordionTriggerProps
  }) => React.ReactNode
  /** Default mode: custom trigger (e.g. button). Provide either render or trigger. When panelWrapper is used, trigger receives triggerProps. */
  trigger?: (props: AccordionRenderProps & { triggerProps?: AccordionTriggerProps }) => React.ReactNode
} & Omit<React.ComponentPropsWithoutRef<'div'>, 'children'>

export const Accordion = ({
  children,
  className,
  defaultExpanded = true,
  expanded: controlledExpanded,
  onExpandedChange,
  panelWrapper,
  render,
  trigger,
  ...props
}: AccordionProps) => {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded)
  const isControlled = controlledExpanded !== undefined
  const isExpanded = isControlled ? controlledExpanded : internalExpanded

  const toggle = () => {
    const next = !isExpanded
    if (!isControlled) setInternalExpanded(next)
    onExpandedChange?.(next)
  }

  const triggerId = useId()
  const panelId = useId()

  const triggerProps: AccordionTriggerProps = {
    id: triggerId,
    onClick: toggle,
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        toggle()
      }
    },
    'aria-expanded': isExpanded,
    'aria-controls': isExpanded ? panelId : undefined,
    tabIndex: 0,
  }

  const panel = (
    <AnimatePresence initial={false}>
      {isExpanded && (
        <motion.div
          id={panelId}
          role="region"
          aria-labelledby={triggerId}
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
          className="overflow-hidden"
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  )

  if (render) {
    return <Fragment>{render({ isExpanded, triggerProps, panel })}</Fragment>
  }

  if (panelWrapper) {
    if (!trigger) {
      throw new Error('Accordion requires trigger when panelWrapper is provided')
    }

    return (
      <Fragment>
        {trigger({ isExpanded, triggerProps })}
        {panelWrapper({ panel, isExpanded })}
      </Fragment>
    )
  }

  if (!trigger) {
    throw new Error('Accordion requires either render or trigger')
  }

  return (
    <div
      data-slot="accordion"
      className={clsx('flex flex-col', className)}
      {...props}
    >
      <button
        type="button"
        id={triggerId}
        onClick={toggle}
        aria-expanded={isExpanded}
        aria-controls={isExpanded ? panelId : undefined}
        className="flex w-full cursor-pointer items-center justify-between rounded-lg p-2 text-left transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
      >
        {trigger({ isExpanded })}
      </button>
      {panel}
    </div>
  )
}
